import { motion } from 'framer-motion';
import { Loader2, WifiOff, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WsStatus } from './types';

interface ConnectionStatusBannerProps {
  wsStatus: WsStatus;
  reconnectAttempt: number;
  onRetry?: () => void;
}

export function ConnectionStatusBanner({
  wsStatus,
  reconnectAttempt,
  onRetry,
}: ConnectionStatusBannerProps) {
  // Only show for reconnecting or error states
  if (wsStatus === 'connected' || wsStatus === 'disconnected' || wsStatus === 'connecting') {
    return null;
  }

  const isReconnecting = wsStatus === 'reconnecting';
  const isError = wsStatus === 'error';

  return (
    <motion.div
      initial={{ opacity: 0, y: -40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -40 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className={cn(
        'fixed top-0 left-0 right-0 z-50 py-3 px-4',
        'flex items-center justify-center gap-3 text-sm font-medium',
        'border-b shadow-sm backdrop-blur-sm',
        isReconnecting && 'bg-amber-50/95 border-amber-200 text-amber-800',
        isError && 'bg-red-50/95 border-red-200 text-red-800'
      )}
    >
      {/* Icon */}
      {isReconnecting ? (
        <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
      ) : (
        <WifiOff className="h-4 w-4 flex-shrink-0" />
      )}

      {/* Message */}
      <span>
        {isReconnecting
          ? `Verbindung wird wiederhergestellt... (Versuch ${reconnectAttempt})`
          : 'Verbindung verloren'}
      </span>

      {/* Retry button (only for error state) */}
      {isError && onRetry && (
        <button
          onClick={onRetry}
          className={cn(
            'ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
            'bg-white border border-red-200 text-red-700',
            'hover:bg-red-50 hover:border-red-300',
            'transition-colors duration-150',
            'text-sm font-medium'
          )}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Erneut versuchen
        </button>
      )}

      {/* Subtle pulsing indicator for reconnecting */}
      {isReconnecting && (
        <div className="absolute left-0 bottom-0 h-0.5 bg-amber-400/50">
          <motion.div
            className="h-full bg-amber-500"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{
              duration: Math.min(1 * Math.pow(2, reconnectAttempt - 1), 30),
              ease: 'linear',
            }}
          />
        </div>
      )}
    </motion.div>
  );
}
